import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Image, FolderOpen, Search, Share2, Shield, Sparkles, Database, Workflow, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedFeaturesSection from "@/components/RelatedFeaturesSection";

const DAM = () => {
  useSEO({
    title: 'Digital Asset Management (DAM) System | Ommni',
    description: 'Centralize, organize, and distribute all your digital assets with Ommni DAM. AI-powered search, version control, and seamless asset sharing for your commerce operations.',
    keywords: 'digital asset management, DAM system, media library, asset management software, brand asset management',
    canonical: 'https://ommni.io/features/dam',
  });

  const features = [
    {
      icon: FolderOpen,
      title: "Centralized Asset Library",
      description: "Store all your images, videos, documents, and media files in one organized location.",
    },
    {
      icon: Search,
      title: "Advanced Search & Tagging",
      description: "Find assets instantly with AI-powered search, custom tags, and metadata filters.",
    },
    {
      icon: Share2,
      title: "Easy Distribution",
      description: "Share assets with teams, partners, and platforms with secure links and permissions.",
    },
    {
      icon: Shield,
      title: "Version Control",
      description: "Track asset versions, maintain edit history, and roll back changes when needed.",
    },
    {
      icon: Sparkles,
      title: "Automatic Optimization",
      description: "Auto-resize, compress, and format images for different channels and devices.",
    },
    {
      icon: Image,
      title: "Rich Media Support",
      description: "Handle images, videos, PDFs, 3D models, and any file type your business needs.",
    },
  ];

  const useCases = [
    {
      title: "Product Photography",
      description: "Organize and distribute product images to all your sales channels instantly.",
    },
    {
      title: "Marketing Materials",
      description: "Store brand assets, campaign creatives, and promotional content in one place.",
    },
    {
      title: "Video Content",
      description: "Manage product videos, tutorials, and marketing videos with ease.",
    },
    {
      title: "Brand Guidelines",
      description: "Keep logos, fonts, and brand resources accessible to your entire team.",
    },
  ];

  return (
    <PageLayout>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Ommni DAM - Digital Asset Management",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Organize, manage, and distribute all your digital assets from one powerful, intuitive platform. Keep your brand consistent across every channel.",
          "offers": {
            "@type": "Offer",
            "price": "500",
            "priceCurrency": "USD",
            "description": "Starting at $500/month"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Centralized Asset Library",
            "Advanced Search & Tagging",
            "Easy Distribution",
            "Version Control",
            "Automatic Optimization",
            "Rich Media Support"
          ]
        })}
      </script>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Image className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Digital Asset Management
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Organize, manage, and distribute all your digital assets from one powerful, intuitive platform. 
              Keep your brand consistent across every channel.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Complete DAM Solution</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 gradient-card">
                  <div className="mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Use Cases Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Common Use Cases</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {useCases.map((useCase, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold mb-2">{useCase.title}</h3>
                  <p className="text-sm text-muted-foreground">{useCase.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Features */}
          <RelatedFeaturesSection
            features={[
              {
                icon: Database,
                title: "Product Information Management",
                description: "Connect product data with rich media assets for complete product content.",
                link: "/features/pim",
              },
              {
                icon: Workflow,
                title: "Workflow Automation",
                description: "Automate asset distribution and transformations with OmmniFlow.",
                link: "/features/ommni-flow",
              },
              {
                icon: Package,
                title: "Inventory Management",
                description: "Link product images with inventory data for unified commerce operations.",
                link: "/features/inventory",
              },
            ]}
          />

          {/* CTA Section */}
          <Card className="p-12 bg-primary/5 border-2 border-primary/20 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Transform Your Digital Asset Workflow
            </h2>
            <p className="text-foreground/80 max-w-2xl mx-auto mb-8 text-lg">
              Stop wasting time searching for files. Get organized with Ommni DAM and empower your teams 
              to work faster and more efficiently.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default DAM;
